/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DateTime} from 'luxon';

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const MEDIUM_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric'
};

const NUMERIC_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric'
};

const MEDIUM_OPTIONS: Intl.DateTimeFormatOptions = {
  ...MEDIUM_DATE_OPTIONS,
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric'
};

const SHORT_OPTIONS: Intl.DateTimeFormatOptions = {
  ...NUMERIC_DATE_OPTIONS,
  hour: 'numeric',
  minute: 'numeric'
};

const FULL_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  weekday: 'long',
  year: 'numeric',
  month: 'long',
  day: 'numeric'
};

const SHORT_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: 'numeric',
  minute: 'numeric'
};

/**
 * Intl treats invalid tags (underscores, empty, unknown) as the runtime
 * locale — a Swedish browser then prints "28 aug. 2026" for an English
 * profile. Always feed DateTimeFormat a real BCP 47 tag.
 */
export function resolveIntlLocale(locale: string | null | undefined): string {
  const normalized = String(locale || 'en-GB').replace(/_/g, '-').trim() || 'en-GB';
  try {
    const canonical = Intl.getCanonicalLocales(normalized)[0] || 'en-GB';
    const supported = Intl.DateTimeFormat.supportedLocalesOf([canonical], {localeMatcher: 'lookup'});
    return supported[0] || 'en-GB';
  } catch (_error) {
    return 'en-GB';
  }
}

export function formatLocaleDate(
  value: Date | string | number,
  format: string,
  locale: string,
  timeZone?: string
): string | null {
  let date: Date;
  if (typeof value === 'string' && DATE_ONLY_PATTERN.test(value) && timeZone) {
    const dateTime = DateTime.fromISO(value, {zone: timeZone});
    if (!dateTime.isValid) {
      return null;
    }
    date = dateTime.toJSDate();
  } else {
    date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }
  }

  let options: Intl.DateTimeFormatOptions;
  switch (format) {
    case 'mediumDate':
    case 'shortDate':
      options = MEDIUM_DATE_OPTIONS;
      break;
    case 'numericDate':
      options = NUMERIC_DATE_OPTIONS;
      break;
    case 'short':
      options = SHORT_OPTIONS;
      break;
    case 'fullDate':
      options = FULL_DATE_OPTIONS;
      break;
    case 'shortTime':
      options = SHORT_TIME_OPTIONS;
      break;
    case 'medium':
    default:
      options = MEDIUM_OPTIONS;
      break;
  }

  const resolvedLocale = resolveIntlLocale(locale);
  try {
    return new Intl.DateTimeFormat(resolvedLocale, timeZone ? {...options, timeZone} : options).format(date);
  } catch (_error) {
    return new Intl.DateTimeFormat(resolvedLocale, options).format(date);
  }
}
